import { createServerFn } from '@tanstack/react-start';
import { authenticatedServerGetEnvelope } from './ssr-fetch';
import type { Purchase, PurchaseListEnvelope } from '@/lib/api/v2/market';

/**
 * Walk the purchase-history cursor to assemble the user's full history. The
 * server caps each response (max 100); pass the max page size so a 101-purchase
 * history takes two calls instead of six. Mirrors the client loop in
 * marketApi.listPurchases so a background refetch produces the same set.
 * `fetchPage` performs one HTTP request per page, so callers (and tests) count
 * calls to it as requests.
 */
export async function collectAllPurchases(
  fetchPage: (path: string) => Promise<PurchaseListEnvelope>
): Promise<Purchase[]> {
  const all: Purchase[] = [];
  let cursor: string | null = null;
  // ponytail: defensive cap — a misbehaving cursor (hasNext never false) would
  // otherwise spin forever on the profile page. 100 pages × 100 = 10000.
  for (let page = 0; page < 100; page++) {
    const path: string = cursor
      ? `/v1/market/purchases?cursor=${encodeURIComponent(cursor)}&limit=100`
      : '/v1/market/purchases?limit=100';
    const env: PurchaseListEnvelope = await fetchPage(path);
    all.push(...env.data);
    if (!env.pagination?.hasNext || !env.pagination?.cursor) break;
    cursor = env.pagination.cursor;
  }
  return all;
}

/** SSR fetch of the user's full purchase history, cookie-forwarded. */
export const fetchPurchasesServer = createServerFn({ method: 'GET' }).handler(async () => {
  'use server';
  return collectAllPurchases((path) => authenticatedServerGetEnvelope<PurchaseListEnvelope>(path));
});

/**
 * What the list loader's blocking await resolves with: the first page (up to
 * 100 rows — the server max) plus the cursor to continue from, so the page can
 * unblock after ONE round trip and let the remaining cursor pages land in the
 * background (the UI pages client-side from whatever the cache holds).
 */
export interface PurchasesFirstPage {
  rows: Purchase[];
  nextCursor: string | null;
}

/**
 * Blocking part of the client-side cold load: one request for the first page.
 * Only users with more than 100 purchases even have a continuation — for
 * everyone else this IS the full history, identical to collectAllPurchases.
 */
export async function fetchPurchasesFirstPage(
  fetchPage: (path: string) => Promise<PurchaseListEnvelope>
): Promise<PurchasesFirstPage> {
  const env: PurchaseListEnvelope = await fetchPage('/v1/market/purchases?limit=100');
  const { hasNext, cursor } = env.pagination ?? {};
  return { rows: env.data, nextCursor: hasNext && cursor ? cursor : null };
}

/**
 * Background continuation of the cursor walk past the first page, merging each
 * page into the ['market','purchases'] cache entry as it lands so the mounted
 * usePurchases observer re-renders with the growing set. The entry converges
 * on the exact array collectAllPurchases would have produced.
 *
 * Guard before every merge: if another writer replaced the entry since our
 * last write — a retry/checkout invalidation refetch, a pull-to-refresh, or a
 * newer walk — that fetch owns the data now and this walk abandons itself
 * rather than clobbering it with a stale append.
 *
 * Failures are swallowed: the partial set stays usable on screen, and the next
 * entry's stale-while-revalidate refresh (or a pull-to-refresh) completes it.
 */
export async function continuePurchasesWalk(
  client: {
    // Minimal QueryClient surface, structural so tests can fake it: the real
    // queryClient passed from the route loader satisfies this shape.
    getQueryData: (queryKey: readonly unknown[]) => unknown;
    setQueryData: (queryKey: readonly unknown[], data: unknown) => void;
  },
  queryKey: readonly unknown[],
  firstPage: PurchasesFirstPage,
  fetchPage: (path: string) => Promise<PurchaseListEnvelope>
): Promise<void> {
  let merged = firstPage.rows;
  let cursor = firstPage.nextCursor;
  // ponytail: same defensive cap as collectAllPurchases — a misbehaving cursor
  // (hasNext never false) must not spin forever. 100 pages × 100 = 10000.
  for (let page = 0; page < 100 && cursor; page++) {
    const env: PurchaseListEnvelope = await fetchPage(
      `/v1/market/purchases?cursor=${encodeURIComponent(cursor)}&limit=100`
    );
    if ((client.getQueryData(queryKey) as Purchase[] | undefined) !== merged) return;
    merged = [...merged, ...env.data];
    client.setQueryData(queryKey, merged);
    const { hasNext, cursor: next } = env.pagination ?? {};
    cursor = hasNext && next ? next : null;
  }
}
